import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, 
  Building2, 
  Users,
  AlertCircle,
  ChevronRight,
  Plus,
  FileCheck,
  Wrench,
  Wallet,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatRupiah, formatRupiahShort, formatPercent, formatChange } from '../utils/format';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, propertiesRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/activities`)
      ]);
      
      setAnalytics(analyticsRes.data);
      setProperties(propertiesRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sample chart data
  const chartData = [
    { month: 'Jan', value: 850 },
    { month: 'Feb', value: 920 },
    { month: 'Mar', value: 1100 },
    { month: 'Apr', value: 980 },
    { month: 'May', value: 1250 },
    { month: 'Jun', value: 1450 },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'lease':
        return <FileCheck className="w-4 h-4" />;
      case 'maintenance':
        return <Wrench className="w-4 h-4" />;
      case 'payment':
        return <Wallet className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getActivityIconClass = (type) => {
    switch (type) {
      case 'lease':
        return 'activity-icon activity-icon-lease';
      case 'maintenance':
        return 'activity-icon activity-icon-maintenance';
      case 'payment':
        return 'activity-icon activity-icon-payment';
      default:
        return 'activity-icon bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A56DB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Portfolio Overview
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, Alex. Here is what's happening across your properties today.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2" data-testid="download-report-btn">
            Download Report
          </Button>
          <Button className="gap-2 bg-[#1A56DB] hover:bg-[#1E40AF]" data-testid="add-property-btn">
            <Plus className="w-4 h-4" />
            Add Property
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Revenue Card */}
        <div className="metric-card col-span-1" data-testid="metric-total-revenue">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#1A56DB]" />
            </div>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
              {formatChange(analytics?.revenue_change)} vs last mo
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Total Revenue (Monthly)</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.total_revenue)}
          </p>
          <div className="flex gap-6 mt-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Residential</p>
              <p className="font-semibold text-slate-700">{formatRupiahShort(analytics?.residential_revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Commercial</p>
              <p className="font-semibold text-slate-700">{formatRupiahShort(analytics?.commercial_revenue)}</p>
            </div>
          </div>
        </div>

        {/* Occupancy Rate Card - Blue */}
        <div className="metric-card metric-card-blue" data-testid="metric-occupancy-rate">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-blue-100 mb-1">Occupancy Rate</p>
          <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatPercent(analytics?.occupancy_rate)}
          </p>
          <p className="text-blue-200 text-sm mt-2">
            {analytics?.vacant_units} units currently vacant across {analytics?.total_properties} properties.
          </p>
        </div>

        {/* Pending Payments Card */}
        <div className="metric-card" data-testid="metric-pending-payments">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-1">Pending Payments</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.pending_payments)}
          </p>
          <p className="text-red-500 text-sm mt-2 font-medium">
            {analytics?.overdue_count} Overdue Invoices
          </p>
        </div>
      </div>

      {/* Charts and Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Income Analysis Chart */}
        <div className="lg:col-span-2 chart-container" data-testid="income-analysis-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Income Analysis
              </h3>
              <p className="text-sm text-slate-500">Yearly revenue progression (2024)</p>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              Last 6 Months
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} />
                <YAxis tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip 
                  formatter={(value) => [formatRupiahShort(value * 1000000), 'Revenue']}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar dataKey="value" fill="#1A56DB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="chart-container" data-testid="recent-activity">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Activity
            </h3>
          </div>
          <div className="space-y-1">
            {activities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className={getActivityIconClass(activity.type)}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{activity.title}</p>
                  <p className="text-xs text-slate-500 truncate">{activity.description}</p>
                  <p className="text-xs text-slate-400 mt-1">{activity.time_ago}</p>
                </div>
              </div>
            ))}
          </div>
          <Button variant="ghost" className="w-full mt-4 text-slate-600" data-testid="view-all-activities">
            View All Activities
          </Button>
        </div>
      </div>

      {/* Active Properties Table */}
      <div className="chart-container" data-testid="active-properties">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Active Properties
          </h3>
          <Button variant="ghost" size="sm" className="gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Property Name</th>
                <th>Occupancy</th>
                <th>Monthly Income</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property) => (
                <tr key={property.id} data-testid={`property-row-${property.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img 
                        src={property.image} 
                        alt={property.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div>
                        <p className="font-semibold text-slate-900">{property.name}</p>
                        <p className="text-xs text-slate-500">{property.location}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-slate-700">
                      {formatPercent((property.occupied_units / property.total_units) * 100, 0)} ({property.occupied_units}/{property.total_units})
                    </span>
                  </td>
                  <td className="font-medium text-slate-900">
                    {formatRupiah(property.monthly_income)}
                  </td>
                  <td>
                    <Badge 
                      className={
                        property.status === 'optimized' 
                          ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-100'
                      }
                    >
                      {property.status === 'optimized' ? 'Optimized' : 'Active'}
                    </Badge>
                  </td>
                  <td>
                    <Button 
                      variant="link" 
                      className="text-[#1A56DB] p-0 h-auto"
                      data-testid={`manage-property-${property.id}`}
                    >
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
